import { themedConfirm } from '../../lib/themed-dialogs';
// Browser Manager Module — správa headless prohlížečů
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  RefreshButton,
  StatCard,
  StatsGrid,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../components/ui';
import {
  CheckCircle2,
  Eye,
  FileImage,
  Globe,
  Monitor,
  Plus,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Types ─────────────────────────────────────────────────────────────────────

interface BrowserConfig {
  default_timeout?: number;
  default_width?: number;
  default_height?: number;
  headless?: boolean;
}

interface BrowserSession {
  id: number;
  type: string;
  url?: string;
  status: 'running' | 'closed';
  started_at: string;
  ended_at?: string;
}

// ── Status Tab ────────────────────────────────────────────────────────────────

function StatusTab() {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['browser-config'],
    queryFn: () =>
      api.mcp<{ config: BrowserConfig }>('browser.chrome_status', {}),
    refetchInterval: 60_000,
  });

  const config = data?.config ?? {};

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-700 text-gray-500'>
          <Monitor size={13} />
          {isLoading ? t('common.loading') : t('browser.ready')}
        </div>
        <button
          onClick={() => refetch()}
          className='p-1.5 text-gray-500 hover:text-white transition-colors'
        >
          <RefreshCw size={14} />
        </button>
      </div>

      <StatsGrid>
        <StatCard
          label='Timeout'
          value={`${config.default_timeout ?? 30000} ms`}
          icon={RefreshCw}
        />
        <StatCard
          label={t('browser.resolution')}
          value={`${config.default_width ?? 1920} × ${config.default_height ?? 1080}`}
          icon={Monitor}
        />
        <StatCard
          label='Headless'
          value={config.headless ? t('browser.yes') : t('browser.no')}
          icon={config.headless ? CheckCircle2 : XCircle}
        />
        <StatCard label='Docker' value={t('browser.ready')} icon={CheckCircle2} />
      </StatsGrid>

      <Card>
        <CardHeader>
          <CardTitle icon={Monitor}>{t('browser.configuration')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          {[
            { label: 'API Endpoint', value: '/apiext/browser/*' },
            { label: 'Docker image', value: 'puppeteer/chrome:latest' },
            { label: t('browser.headlessMode'), value: config.headless ? t('browser.allowed') : t('browser.forbidden') },
            { label: t('browser.windowWidth'), value: `${config.default_width ?? 1920}px` },
            { label: t('browser.windowHeight'), value: `${config.default_height ?? 1080}px` },
            { label: 'Timeout', value: `${config.default_timeout ?? 30000}ms` },
          ].map(({ label, value }) => (
            <div key={label} className='flex items-center justify-between'>
              <span className='text-xs text-gray-500'>{label}</span>
              <code className='text-xs text-gray-300 font-mono'>{value}</code>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Sessions Tab ──────────────────────────────────────────────────────────────

function SessionsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['browser-sessions'],
    queryFn: () =>
      api.mcp<{ sessions: BrowserSession[]; total: number }>(
        'browser.sessions_list',
        {},
      ),
    refetchInterval: 30_000,
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) =>
      api.mcp('browser.chrome_close', { sessionId: id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['browser-sessions'] });
      toast.success(t('browser.sessionClosed'));
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const sessions = data?.sessions ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2'>
        <Button onClick={() => setShowNew(true)} icon={<Plus size={14} />}>
          {t('browser.newSession')}
        </Button>
        <RefreshButton onRefresh={refetch} />
      </div>

      {isLoading && <div className='py-8 text-center text-gray-500 text-sm'>{t('common.loading')}</div>}
      {error && (
        <div className='py-8 text-center text-red-400 text-sm'>
          {t('browser.error', { message: (error as Error).message })}
        </div>
      )}

      {!isLoading && !error && sessions.length === 0 && (
        <EmptyState
          icon={<Eye size={24} />}
          title={t('browser.noSessions')}
          description={t('browser.noSessionsDesc')}
        />
      )}

      {sessions.length > 0 && (
        <div className='border border-gray-800 rounded-xl overflow-hidden'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b border-gray-800 bg-gray-900/30'>
                {['ID', t('browser.colType'), 'URL', 'Status', 'Start', ''].map((h) => (
                  <th key={h} className='text-left px-4 py-2.5 text-xs font-medium text-gray-600'>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-800/40'>
              {sessions.map((s) => (
                <tr key={s.id} className='hover:bg-gray-800/20 transition-colors group'>
                  <td className='px-4 py-2.5'>
                    <code className='text-xs text-gray-300 font-mono'>#{s.id}</code>
                  </td>
                  <td className='px-4 py-2.5 text-xs text-blue-400'>{s.type}</td>
                  <td className='px-4 py-2.5'>
                    <span className='text-xs text-gray-400 truncate max-w-[150px] inline-block'>
                      {s.url ?? '-'}
                    </span>
                  </td>
                  <td className='px-4 py-2.5'>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        s.status === 'running'
                          ? 'bg-green-500/15 text-green-400'
                          : 'bg-gray-500/15 text-gray-400'
                      }`}
                    >
                      {s.status === 'running' ? t('browser.statusRunning') : t('browser.statusClosed')}
                    </span>
                  </td>
                  <td className='px-4 py-2.5 text-xs text-gray-500'>
                    {new Date(s.started_at).toLocaleString('cs-CZ', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </td>
                  <td className='px-4 py-2.5'>
                    <div className='opacity-0 group-hover:opacity-100 transition-opacity'>
                      <button
                        onClick={async () => await themedConfirm(t('browser.closeSessionConfirm')) && deleteMut.mutate(s.id)}
                        className='p-1.5 rounded text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors'
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && (
            <div className='px-4 py-2 text-xs text-gray-600 border-t border-gray-800/50'>
              {t('browser.sessionsTotal', { count: data.total })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Screenshot Tab ────────────────────────────────────────────────────────────

function ScreenshotTab() {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [form, setForm] = useState({
    selector: '',
    fullPage: false,
    format: 'png' as 'png' | 'jpeg',
  });

  const mut = useMutation({
    mutationFn: () =>
      api.mcp<{ message?: string }>('browser.chrome_screenshot', {
        url,
        ...(form.selector ? { selector: form.selector } : {}),
        fullPage: form.fullPage,
        format: form.format,
      }),
    onSuccess: (data) => {
      toast.success(data.message || t('browser.screenshotTaken'));
      setUrl('');
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='max-w-xl space-y-4'>
      <div>
        <label className='block text-xs text-gray-500 mb-1'>URL *</label>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder='https://example.com'
          className='w-full bg-gray-900/40 border border-gray-800 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-600'
        />
      </div>

      <div className='space-y-3'>
        <div>
          <label className='block text-xs text-gray-500 mb-1'>{t('browser.cssSelectorOptional')}</label>
          <input
            value={form.selector}
            onChange={(e) => setForm((f) => ({ ...f, selector: e.target.value }))}
            placeholder='.element-selector'
            className='w-full bg-gray-900/40 border border-gray-800 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-600'
          />
        </div>

        <div className='flex items-center gap-2'>
          <input
            type='checkbox'
            id='fullPage'
            checked={form.fullPage}
            onChange={(e) => setForm((f) => ({ ...f, fullPage: e.target.checked }))}
            className='rounded border-gray-700 bg-gray-900/40 text-blue-600 focus:ring-blue-600/30'
          />
          <label htmlFor='fullPage' className='text-sm text-gray-300'>
            {t('browser.fullPageScreenshot')}
          </label>
        </div>

        <div>
          <label className='block text-xs text-gray-500 mb-1'>{t('browser.format')}</label>
          <div className='flex gap-2'>
            {(['png', 'jpeg'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setForm((f2) => ({ ...f2, format: f }))}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.format === f
                    ? 'bg-blue-600/20 border border-blue-500/30 text-blue-400'
                    : 'bg-gray-900/40 border border-gray-800 text-gray-400 hover:bg-gray-800'
                }`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Button
        onClick={() => mut.mutate()}
        disabled={!url || mut.isPending}
        className='w-full'
        icon={<FileImage size={16} />}
      >
        {t('browser.takeScreenshot')}
      </Button>
    </div>
  );
}

// ── Scrape Tab ────────────────────────────────────────────────────────────────

function ScrapeTab() {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [form, setForm] = useState({ selector: '', extract: 'text' });

  const mut = useMutation({
    mutationFn: () =>
      api.mcp<{ message?: string }>('browser.chrome_scrape', {
        url,
        ...(form.selector ? { selector: form.selector } : {}),
        extract: form.extract.split(',').map((s) => s.trim()).filter(Boolean),
      }),
    onSuccess: (data) => {
      toast.success(data.message || t('browser.dataScraped'));
      setUrl('');
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='max-w-xl space-y-4'>
      <div>
        <label className='block text-xs text-gray-500 mb-1'>URL *</label>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder='https://example.com'
          className='w-full bg-gray-900/40 border border-gray-800 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-600'
        />
      </div>

      <div className='space-y-3'>
        <div>
          <label className='block text-xs text-gray-500 mb-1'>{t('browser.cssSelectorOptional')}</label>
          <input
            value={form.selector}
            onChange={(e) => setForm((f) => ({ ...f, selector: e.target.value }))}
            placeholder='.element-selector'
            className='w-full bg-gray-900/40 border border-gray-800 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-600'
          />
        </div>

        <div>
          <label className='block text-xs text-gray-500 mb-1'>{t('browser.extractComma')}</label>
          <input
            value={form.extract}
            onChange={(e) => setForm((f) => ({ ...f, extract: e.target.value }))}
            placeholder='text, href, src, class'
            className='w-full bg-gray-900/40 border border-gray-800 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-600'
          />
        </div>
      </div>

      <Button
        onClick={() => mut.mutate()}
        disabled={!url || mut.isPending}
        className='w-full'
        icon={<Globe size={16} />}
      >
        {t('browser.runScraping')}
      </Button>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function BrowserPage() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        icon={<Monitor size={24} />}
        title='Browser Manager'
        description={t('browser.description')}
      />

      <Tabs defaultTab='status'>
        <TabList>
          <Tab id='status' icon={<Monitor size={14} />}>Status</Tab>
          <Tab id='sessions' icon={<Eye size={14} />}>Sessions</Tab>
          <Tab id='screenshot' icon={<FileImage size={14} />}>Screenshot</Tab>
          <Tab id='scrape' icon={<Globe size={14} />}>Scrape</Tab>
        </TabList>

        <TabPanel id='status'>
          <StatusTab />
        </TabPanel>
        <TabPanel id='sessions'>
          <SessionsTab />
        </TabPanel>
        <TabPanel id='screenshot'>
          <ScreenshotTab />
        </TabPanel>
        <TabPanel id='scrape'>
          <ScrapeTab />
        </TabPanel>
      </Tabs>
    </div>
  );
}
